import React from "react";
import { FaShieldAlt, FaLock, FaEye, FaUserCheck } from "react-icons/fa";
import SEO from "../../components/common/SEO";
import Breadcrumb from "../../components/common/Breadcrumb";

const Privacy = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Privacy Policy - Arvix Creative",
    "description": "Privacy Policy for Arvix Creative. Learn how we collect, use, and protect your personal information when you visit our website or use our creative services.",
    "url": "https://arvixcreative.com/privacy",
    "datePublished": "2024-01-15",
    "dateModified": "2025-10-03",
    "publisher": {
      "@type": "Organization",
      "name": "Arvix Creative",
      "logo": {
        "@type": "ImageObject",
        "url": "https://arvixcreative.com/logo512.png"
      }
    }
  };

  return (
    <>
      <SEO 
        title="Privacy Policy - Arvix Creative | Data Protection & Privacy"
        description="Privacy Policy for Arvix Creative. Learn how we collect, use, and protect your personal information when you visit our website or use our creative services. Last updated: October 3, 2025."
        keywords="privacy policy, data protection, privacy, Arvix Creative privacy, GDPR, data privacy, personal information protection"
        url="https://arvixcreative.com/privacy"
        structuredData={structuredData}
      />
      <div className="min-h-screen relative overflow-hidden">
      {/* Consistent Background */}
      <div className="fixed inset-0 -z-20 bg-gradient-to-br from-red-50/80 via-red-100/60 to-red-200/40"></div>
      <div className="fixed -top-40 -left-32 -z-20 w-[36rem] h-[36rem] rounded-full bg-gradient-to-tr from-red-200/40 to-designer-primary/20 blur-3xl animate-pulse-slow"></div>
      <div className="fixed -bottom-40 -right-32 -z-20 w-[40rem] h-[40rem] rounded-full bg-gradient-to-tr from-designer-primary/20 to-red-300/30 blur-3xl animate-pulse-slow"></div>
      <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 -z-20 w-[60rem] h-[60rem] rounded-full bg-gradient-to-tr from-red-100/10 to-designer-primary/5 blur-3xl"></div>
      
      <div className="relative z-10 w-full py-16 md:py-24">
        <Breadcrumb items={[{ label: "Privacy Policy" }]} className="mb-8" />

        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-3 mb-4">
            <FaShieldAlt className="text-4xl text-designer-primary" />
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-designer-dark text-balance">
              Privacy Policy
            </h1>
          </div>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Your privacy is important to us. This policy explains how we collect, use, and protect your information.
          </p>
          <p className="text-sm text-gray-500 mt-2">Last updated: 3rd October 2025</p>
        </div>

        {/* Content */}
        <div>
          <div className="bg-white rounded-3xl p-6 sm:p-8 md:p-12 shadow-red shadow-red-hover border border-gray-200">
            
            {/* Introduction */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-designer-dark mb-4 flex items-center gap-3">
                <FaLock className="text-designer-primary" />
                Introduction
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Arvix Creative ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy 
                explains how we collect, use, disclose, and safeguard your information when you visit our website 
                or use our services. Please read this privacy policy carefully.
              </p>
            </section>

            {/* Information We Collect */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-designer-dark mb-4 flex items-center gap-3">
                <FaEye className="text-designer-primary" />
                Information We Collect
              </h2>
              
              <h3 className="text-xl font-semibold text-designer-dark mb-3">Personal Information</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                We may collect personal information that you voluntarily provide to us when you:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
                <li>Contact us through our website forms</li>
                <li>Request a quote or consultation</li>
                <li>Subscribe to our newsletter</li>
                <li>Engage with our social media content</li>
                <li>Communicate with us via email or phone</li>
              </ul>

              <h3 className="text-xl font-semibold text-designer-dark mb-3">Automatically Collected Information</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                We may automatically collect certain information about your device and usage patterns:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>IP address and location data</li>
                <li>Browser type and version</li>
                <li>Pages visited and time spent on our website</li>
                <li>Referring website information</li>
                <li>Device information and operating system</li>
              </ul>
            </section>

            {/* How We Use Information */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-designer-dark mb-4 flex items-center gap-3">
                <FaUserCheck className="text-designer-primary" />
                How We Use Your Information
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We use the information we collect to:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>Provide and improve our creative services</li>
                <li>Respond to your inquiries and requests</li>
                <li>Send you project updates and communications</li>
                <li>Analyze website usage to improve user experience</li>
                <li>Comply with legal obligations</li>
                <li>Protect against fraud and unauthorized access</li>
              </ul>
            </section>

            {/* Information Sharing */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-designer-dark mb-4">Information Sharing</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We do not sell, trade, or otherwise transfer your personal information to third parties without 
                your consent, except in the following circumstances:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>With your explicit consent</li>
                <li>To comply with legal requirements</li>
                <li>To protect our rights and prevent fraud</li>
                <li>With trusted service providers who assist in our operations</li>
              </ul>
            </section>

            {/* Data Security */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-designer-dark mb-4">Data Security</h2>
              <p className="text-gray-700 leading-relaxed">
                We implement appropriate security measures to protect your personal information against 
                unauthorized access, alteration, disclosure, or destruction. However, no method of transmission 
                over the internet is 100% secure, and we cannot guarantee absolute security.
              </p>
            </section>

            {/* Your Rights */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-designer-dark mb-4">Your Rights</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                You have the right to:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>Access your personal information</li>
                <li>Correct inaccurate information</li>
                <li>Request deletion of your information</li>
                <li>Object to processing of your information</li>
                <li>Withdraw consent at any time</li>
              </ul>
            </section>

            {/* Changes to Policy */}
            <section>
              <h2 className="text-2xl font-bold text-designer-dark mb-4">Changes to This Policy</h2>
              <p className="text-gray-700 leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any changes by 
                posting the new Privacy Policy on this page and updating the "Last updated" date. Your continued 
                use of our services after any modifications constitutes acceptance of the updated policy.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default Privacy;
